/**
 * The analog heat gauge. SVG, because it stays crisp at small sizes and a
 * needle is one cheap transform.
 *
 * The needle is driven straight off heat with no easing. A lagging needle
 * would misrepresent risk at the exact moment risk matters.
 */

import { HEAT_CAUTION, HEAT_CRITICAL } from '../data/balance.ts';

const NS = 'http://www.w3.org/2000/svg';

/** 240 degree sweep, from lower-left round to lower-right. */
const START_DEG = -210;
const SWEEP_DEG = 240;

const CX = 100;
const CY = 92;
const R = 74;

export interface Gauge {
  svg: SVGSVGElement;
  needle: SVGGElement;
}

function el<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

function pointAt(value: number, radius: number): [number, number] {
  const deg = START_DEG + (value / 100) * SWEEP_DEG;
  const rad = (deg * Math.PI) / 180;
  return [CX + Math.cos(rad) * radius, CY + Math.sin(rad) * radius];
}

function arc(from: number, to: number, radius: number, width: number, stroke: string): SVGPathElement {
  const [x1, y1] = pointAt(from, radius);
  const [x2, y2] = pointAt(to, radius);
  const large = ((to - from) / 100) * SWEEP_DEG > 180 ? 1 : 0;
  return el('path', {
    d: `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2}`,
    fill: 'none',
    stroke,
    'stroke-width': width,
    'stroke-linecap': 'butt',
  });
}

export function createGauge(mount: HTMLElement): Gauge {
  const svg = el('svg', {
    viewBox: '0 0 200 132',
    class: 'gauge',
    role: 'img',
    'aria-hidden': 'true',
  });

  // Bands. Colour is a reinforcement here, never the only signal.
  svg.append(
    arc(0, HEAT_CAUTION, R, 12, 'var(--panel-recess)'),
    arc(HEAT_CAUTION, HEAT_CRITICAL, R, 12, 'var(--amber)'),
    arc(HEAT_CRITICAL, 100, R, 12, 'var(--oxblood)'),
  );

  // Graduations every 10, longer every 20.
  for (let v = 0; v <= 100; v += 10) {
    const long = v % 20 === 0;
    const [x1, y1] = pointAt(v, R - 8);
    const [x2, y2] = pointAt(v, R - (long ? 18 : 13));
    svg.append(
      el('line', {
        x1,
        y1,
        x2,
        y2,
        stroke: 'var(--ink)',
        'stroke-width': long ? 1.6 : 1,
        opacity: 0.7,
      }),
    );
    if (long) {
      const [tx, ty] = pointAt(v, R - 29);
      const label = el('text', {
        x: tx,
        y: ty + 4,
        'text-anchor': 'middle',
        fill: 'var(--ink-soft)',
        'font-size': 10,
        'font-family': 'var(--font-mono)',
      });
      label.textContent = String(v);
      svg.append(label);
    }
  }

  // Needle, with a counterweight tail past the pivot.
  const needle = el('g', { class: 'gauge__needle' });
  needle.append(
    el('line', {
      x1: CX,
      y1: CY,
      x2: CX,
      y2: CY - R + 10,
      stroke: 'var(--ink)',
      'stroke-width': 2.6,
      'stroke-linecap': 'round',
    }),
    el('line', {
      x1: CX,
      y1: CY,
      x2: CX,
      y2: CY + 15,
      stroke: 'var(--ink)',
      'stroke-width': 4,
      'stroke-linecap': 'round',
    }),
  );
  svg.append(needle);

  svg.append(
    el('circle', { cx: CX, cy: CY, r: 7, fill: 'var(--ink)' }),
    el('circle', { cx: CX, cy: CY, r: 3, fill: 'var(--panel)' }),
  );

  mount.append(svg);
  return { svg, needle };
}

/** Points the needle at `heat`. No easing, by design. */
export function setGauge(gauge: Gauge, heat: number): void {
  const clamped = heat < 0 ? 0 : heat > 100 ? 100 : heat;
  // The needle art points straight up, which is the middle of the sweep.
  const deg = START_DEG + (clamped / 100) * SWEEP_DEG + 90;
  gauge.needle.setAttribute('transform', `rotate(${deg.toFixed(2)} ${CX} ${CY})`);
}
